'use es6';

import { forwardRef, memo, useState, useEffect } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UIButton from 'UIComponents/button/UIButton';
import UIList from 'UIComponents/list/UIList';
import withVisibilityChecker from 'simple-menus-lib/components/menu/decorators/withVisibilityChecker';
import withPromotingMenuDropTargets from 'simple-menus-lib/components/menu/decorators/withPromotingMenuDropTargets';
import withConnectedMenuNodeDivider from 'simple-menus-lib/components/menu/decorators/withConnectedMenuNodeDivider';
import menuDividerDropTarget from 'simple-menus-lib/components/dnd/decorators/menuDividerDropTarget';
import MenuNodeTypes from 'simple-menus-core/enums/MenuNodeTypes';
import { MENU_TREE_INDENTATION, MENU_DIVIDER_MARGIN_OFFSET } from 'simple-menus-core/constants/constants';
import UsageTracker from 'simple-menus-lib/utils/usageTracker/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const actions = [MenuNodeTypes.NO_LINK, MenuNodeTypes.PAGE_LINK, MenuNodeTypes.PAGE_LINK_WITH_PARAMS, MenuNodeTypes.URL_LINK];
const PopoverActionsContent = ({
  addNode,
  setIsOpen
}) => {
  const handleNodeCreate = type => {
    addNode(type);
    UsageTracker.track('addMenuItem', {
      type,
      method: 'divider'
    });
    setIsOpen(false);
  };
  const handlers = {
    [MenuNodeTypes.NO_LINK]: () => handleNodeCreate(MenuNodeTypes.NO_LINK),
    [MenuNodeTypes.PAGE_LINK]: () => handleNodeCreate(MenuNodeTypes.PAGE_LINK),
    [MenuNodeTypes.PAGE_LINK_WITH_PARAMS]: () => {
      handleNodeCreate(MenuNodeTypes.PAGE_LINK_WITH_PARAMS);
    },
    [MenuNodeTypes.URL_LINK]: () => handleNodeCreate(MenuNodeTypes.URL_LINK)
  };
  const buttons = actions.map(action => {
    return /*#__PURE__*/_jsx(UIButton, {
      onClick: handlers[action],
      use: "unstyled",
      className: "simple-menus-menu-node-divider-option-button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `simpleMenusLib.components.menuItemDivider.addItems.${action}`
      })
    }, action);
  });
  return /*#__PURE__*/_jsx(UIList, {
    className: "p-y-2",
    children: buttons
  });
};
const MenuNodeDivider = /*#__PURE__*/memo( /*#__PURE__*/forwardRef((props, ref) => {
  const [isHovering, setIsHovering] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const {
    depth,
    depthToNextSibling,
    dropTargets,
    canDrop,
    hasChildren,
    isDragging,
    isLastChild,
    isOver,
    isParentDragging,
    readOnly,
    addNode,
    connectDropTarget
  } = props;
  const nodeClassNames = classNames('simple-menus-menu-node-divider', menuIsOpen && 'is--open', isOver && 'is--over', readOnly && 'is--readOnly');
  const dividerClassNames = 'divider' + (isOver && !canDrop ? " no--drop" : "");
  const hasDropTargets = !(hasChildren || !isLastChild || !depth || !isDragging);
  const showMenuButton = menuIsOpen || isHovering && !isOver && !isDragging && !isParentDragging;
  let bodyStyles = {
    width: `calc(100% + ${(depth + MENU_DIVIDER_MARGIN_OFFSET) * MENU_TREE_INDENTATION}px)`,
    marginLeft: `-${(depth + MENU_DIVIDER_MARGIN_OFFSET) * MENU_TREE_INDENTATION}px`
  };
  if (isDragging && !hasDropTargets) {
    bodyStyles = {
      width: '100%',
      marginLeft: '0'
    };
  }
  if (isDragging && hasDropTargets) {
    bodyStyles = {
      width: `calc(100% + ${depthToNextSibling * MENU_TREE_INDENTATION}px)`,
      marginLeft: `-${depthToNextSibling * MENU_TREE_INDENTATION}px`
    };
  }
  useEffect(() => {
    const handleMouseClick = ({
      target
    }) => {
      if (menuIsOpen) {
        const domNode = ref.current;
        if (!domNode || !domNode.contains(target)) {
          setMenuIsOpen(false);
        }
      }
    };
    const handleScroll = () => {
      if (menuIsOpen) {
        setMenuIsOpen(false);
      }
    };
    document.addEventListener('click', handleMouseClick);
    document.addEventListener('wheel', handleScroll);
    return () => {
      document.removeEventListener('click', handleMouseClick);
      document.removeEventListener('wheel', handleScroll);
    };
  }, [menuIsOpen, ref]);
  const toggleOpen = () => {
    setMenuIsOpen(isOpen => !isOpen);
  };
  const handleOpenChange = ({
    target: {
      value
    }
  }) => {
    setMenuIsOpen(value);
  };
  const handleMouseEnter = () => {
    if (readOnly) return;
    setIsHovering(true);
  };
  const handleMouseLeave = () => {
    setIsHovering(false);
  };
  return connectDropTarget( /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsxs("div", {
      ref: ref,
      className: nodeClassNames,
      style: bodyStyles,
      onClick: toggleOpen,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      children: [dropTargets, showMenuButton && /*#__PURE__*/_jsx(UIPopover, {
        open: menuIsOpen,
        onOpenChange: handleOpenChange,
        placement: "bottom right",
        content: /*#__PURE__*/_jsx(PopoverActionsContent, {
          addNode: addNode,
          setIsOpen: setMenuIsOpen
        }),
        closeOnOutsideClick: false,
        children: /*#__PURE__*/_jsx(UIIconButton, {
          size: "extra-small",
          className: "add-button",
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "add"
          })
        })
      }), /*#__PURE__*/_jsx("div", {
        className: dividerClassNames
      })]
    })
  }));
}), (prevProps, nextProps) => {
  return !nextProps.isVisible || prevProps.depth === nextProps.depth && prevProps.depthToNextSibling === nextProps.depthToNextSibling && prevProps.dropTargets === nextProps.dropTargets && prevProps.canDrop === nextProps.canDrop && prevProps.hasChildren === nextProps.hasChildren && prevProps.isDragging === nextProps.isDragging && prevProps.isLastChild === nextProps.isLastChild && prevProps.isOver === nextProps.isOver && prevProps.isParentDragging === nextProps.isParentDragging && prevProps.isVisible === nextProps.isVisible && prevProps.readOnly === nextProps.readOnly && prevProps.addNode === nextProps.addNode && prevProps.connectDropTarget === nextProps.connectDropTarget;
});
MenuNodeDivider.propTypes = {
  depth: PropTypes.number.isRequired,
  depthToNextSibling: PropTypes.number,
  dropTargets: PropTypes.node,
  canDrop: PropTypes.bool,
  hasChildren: PropTypes.bool,
  isDragging: PropTypes.bool,
  isLastChild: PropTypes.bool,
  isOver: PropTypes.bool,
  isParentDragging: PropTypes.bool,
  isVisible: PropTypes.bool,
  readOnly: PropTypes.bool,
  addNode: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired
};
export default compose(withConnectedMenuNodeDivider(), withPromotingMenuDropTargets(), menuDividerDropTarget(), withVisibilityChecker())(MenuNodeDivider);